import { applySnapshot, getSnapshot, types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";

export const DataRow = types
  .model({
    tab: types.string,
    type: types.string,
    fieldName: types.string,
    value: types.string,
    prevValue: types.string,
    isNew: types.boolean,
    isUpdate: types.boolean,
    isRemove: types.boolean,
  })
  .named("DataRow");

export const RowData = types.compose(
  DataRow,
  types.model({ rows: types.maybeNull(types.array(DataRow)) }).named("RowData")
);

export const OrderLog = types
  .model({
    id: types.string,
    user: types.string,
    dateText: types.string,
    time: types.string,
    rows: types.array(RowData),
  })
  .named("OrderLog");

export const OrderLogsStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      loading: types.boolean,
      loadingNext: types.optional(types.boolean, false),
      data: types.array(OrderLog),
      hasMore: types.boolean,
      // курсор следующей страницы приходит с сервера: записи журнала,
      // из которых нечего показать, пропускаются, поэтому по id последней
      // видимой строки позицию не восстановить
      cursor: types.optional(types.string, ""),
    })
  )
  .actions((self) => ({
    load: flow(function* (orderId: string) {
      try {
        self.loading = true;

        const page: OrderLogsPage = yield self.transport.get<OrderLogsPage>(apiUrls.orders.logs, {
          params: { orderId, limit: LIMIT },
        });

        applySnapshot(self.data, page.rows || []);
        self.cursor = page.nextCursor || "";
        self.hasMore = !!page.hasMore;

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      } finally {
        self.loading = false;
      }
    }),

    loadNext: flow(function* (orderId: string) {
      if (self.loadingNext || !self.hasMore) {
        return false;
      }

      try {
        self.loadingNext = true;

        const page: OrderLogsPage = yield self.transport.get<OrderLogsPage>(apiUrls.orders.logs, {
          params: { orderId, cursor: self.cursor, limit: LIMIT },
        });

        applySnapshot(self.data, [...getSnapshot(self.data), ...(page.rows || [])]);
        self.cursor = page.nextCursor || "";
        self.hasMore = !!page.hasMore;

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      } finally {
        self.loadingNext = false;
      }
    }),
  }))
  .named("OrderLogsStore");

const LIMIT = 20;

interface OrderLogsPage {
  rows: OrderLogSnapshotType[];
  nextCursor: string;
  hasMore: boolean;
}

export type DataRowSnapshotType = typeof RowData.SnapshotType;
export type OrderLogType = typeof OrderLog.Type;
export type OrderLogSnapshotType = typeof OrderLog.SnapshotType;
export type OrderLogsStoreType = typeof OrderLogsStore.Type;
